import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { getAuxiliaryActivityForDelete } from 'common/activeElements/modals/ModalDeleteActivity/utils/getAuxiliaryActivityForDelete';
import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import personalTimelineStore from 'pages/CrewMemberDetails/components/PersonalTimeline/PersonalTimelineStore';

import { UPDATE_ABSENCE_AVAILABILITY } from './gql/updateAbsenceAvailability';

export const useActivityMutations = () => {
	const active = useActiveElements();

	// Local state to simulate loading for stub actions
	const [stubLoading, setStubLoading] = useState(false);

	const [updateAvailability, { loading: loadingAvailability }] = useMutation(
		UPDATE_ABSENCE_AVAILABILITY,
		{
			onCompleted: async () => {
				// TODO: CHECK IF HERE IS DATE DEPENDENCY
				await personalTimelineStore.fetchActivities();
			},
			onError: (err) => {
				// Handle error reporting here (e.g., toast notification)
				console.error('Failed to update availability', err);
			},
		}
	);

	/**
	 * Helper to simulate network delay for stubs
	 */
	const runStubAction = () => {
		setStubLoading(true);
		// Return promise to allow caller to await if needed
		return new Promise((resolve) => {
			setTimeout(() => {
				notificationStore.setNotification(standardSuccess);

				setStubLoading(false);
				resolve({ success: true });
			}, 300); // 0.3s delay
		});
	};

	// --- ABSENCE HANDLERS ---

	const handleDeleteAbsence = (absenceId, { isRRO = false, fromTimestamp } = {}) => {
		if (!active.registry.has(MODAL_NAMES.deleteAbsence)) {
			return;
		}

		active.setActiveElement(MODAL_NAMES.deleteAbsence, {
			initialData: { id: absenceId, isRRO, fromTimestamp },
		});
	};

	const handleToggleAvailability = (data) => {
		if (loadingAvailability || stubLoading) return;
		const { id, input } = data;

		updateAvailability({
			variables: { id, input },
		});
	};

	const handleDeleteWAActivity = (payload) => {
		if (!active.registry.has(MODAL_NAMES.updateAuxiliaryWAStatus)) {
			return;
		}

		active.setActiveElement(MODAL_NAMES.updateAuxiliaryWAStatus, {
			initialData: getAuxiliaryActivityForDelete(payload),
		});
	};

	const handleUpdateAbsence = (payload) => {
		if (!active.registry.has(MODAL_NAMES.absence)) {
			return;
		}

		active.setActiveElement(MODAL_NAMES.absence, {
			readOnly: false,
			isEditMode: true,
			initialData: payload,
		});
	};

	const handleManualReconciliation = (payload) => {
		if (!active.registry.has(MODAL_NAMES.manualReconciliation)) {
			return;
		}

		active.setActiveElement(MODAL_NAMES.manualReconciliation, {
			initialData: {
				workAssignmentId: payload.firstWorkAssignmentId,
				workAssignmentVersion: payload.firstWorkAssignmentVersion,
				fromTimestamp: payload.fromTimestamp,
				toTimestamp: payload.toTimestamp,
				fromStationName: payload.fromStation?.name || '',
				toStationName: payload.toStation?.name || '',
				timezone: payload.timezone,
			},
		});
	};

	// --- WORK ASSIGNMENT HANDLERS ---

	const handleUpdateWorkAssignment = (payload) => {
		if (stubLoading) return;
		runStubAction('Update Work Assignment Stub', payload);
	};

	// --- BREAK HANDLERS ---

	const handleUpdateBreak = (payload) => {
		if (stubLoading) return;
		runStubAction('Update Break Stub', payload);
	};

	return {
		// Absence
		toggleAvailability: handleToggleAvailability,
		updateAbsence: handleUpdateAbsence,
		deleteAbsence: handleDeleteAbsence,

		// Work Assignment
		updateWorkAssignment: handleUpdateWorkAssignment,
		deleteWorkAActivity: handleDeleteWAActivity,
		manualReconciliation: handleManualReconciliation,

		// Break
		updateBreak: handleUpdateBreak,

		// Combined State (True if either Real Mutation OR Stub is running)
		loading: loadingAvailability || stubLoading,
	};
};
